import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Loader2, UserRound, FileText, CreditCard, BellRing, Sparkles } from "lucide-react";

const STATUS = {
  ok: { label: "À jour", cls: "bg-emerald-100 text-emerald-700" },
  due: { label: "Solde ouvert", cls: "bg-amber-100 text-amber-700" },
  overdue: { label: "En retard", cls: "bg-rose-100 text-rose-700" },
};

export default function Clients() {
  const { companyId, cur } = useCompany();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", type: "company", phone: "", email: "", tax_id: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/customers", { params: { company_id: companyId } }).then(({ data }) => setItems(data.customers));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/customers", form, { params: { company_id: companyId } });
      toast.success("Client créé");
      setOpen(false); setForm({ name: "", type: "company", phone: "", email: "", tax_id: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/customers/${id}`, { params: { company_id: companyId } });
      setDetail(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients (CRM)" subtitle="Chiffre d'affaires, comportement de paiement et historique par client." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full active:scale-95 transition-transform" data-testid="new-client-button"><Plus className="w-4 h-4 mr-1" /> Nouveau client</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display">Nouveau client</DialogTitle><DialogDescription>Ajoutez un client à votre carnet.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" /></div>
                <div className="space-y-1"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" /></div>
              </div>
              <div className="space-y-1"><Label>N° fiscal</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="client-tax-input" /></div>
            </div>
            <DialogFooter><Button onClick={save} disabled={saving || !form.name} className="rounded-full" data-testid="save-client-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />

      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucun client" hint="Ajoutez votre premier client." />
          : <Table>
              <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">CA total</TableHead><TableHead>Dernier achat</TableHead><TableHead className="text-right">Solde dû</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((c) => {
                  const s = STATUS[c.status] || STATUS.ok;
                  return (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => openDetail(c.id)} data-testid={`client-row-${c.id}`}>
                      <TableCell className="font-medium flex items-center gap-2"><UserRound className="w-4 h-4 text-muted-foreground" />{c.name}</TableCell>
                      <TableCell className="text-muted-foreground">{c.phone || c.email || "—"}</TableCell>
                      <TableCell className="text-right tabular"><Money value={c.total_billed || 0} cur={cur} /></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{c.last_purchase ? fmtDate(c.last_purchase) : "—"}</TableCell>
                      <TableCell className="text-right"><Money value={c.balance} cur={cur} className={c.balance > 0 ? "text-rose-600 font-semibold" : ""} /></TableCell>
                      <TableCell><span className={`text-xs font-medium rounded-full px-2.5 py-1 ${s.cls}`} data-testid={`client-status-${c.id}`}>{s.label}</span></TableCell>
                    </TableRow>);
                })}
              </TableBody>
            </Table>}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-popover max-w-2xl" data-testid="client-detail">
          {detail && <>
            <DialogHeader><DialogTitle className="font-display">{detail.customer.name}</DialogTitle><DialogDescription>{detail.customer.phone || detail.customer.email || "Fiche client"}</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="p-3 border-border"><div className="text-xs text-muted-foreground">CA total</div><div className="font-display font-bold"><Money value={detail.totals.billed} cur={cur} /></div></Card>
              <Card className="p-3 border-border"><div className="text-xs text-muted-foreground">Solde dû</div><div className="font-display font-bold text-rose-600"><Money value={detail.totals.balance} cur={cur} /></div></Card>
              <Card className="p-3 border-border"><div className="text-xs text-muted-foreground">Dernière facture</div><div className="font-display font-bold text-sm">{detail.last_invoice_date ? fmtDate(detail.last_invoice_date) : "—"}</div></Card>
              <Card className="p-3 border-border"><div className="text-xs text-muted-foreground">Délai moyen paiement</div><div className="font-display font-bold text-sm">{detail.avg_payment_delay != null ? `${detail.avg_payment_delay} j` : "—"}</div></Card>
            </div>

            {detail.recommend_relance && (
              <div className="flex items-center justify-between gap-3 bg-accent/50 border border-primary/30 rounded-md px-3 py-2.5" data-testid="client-reco">
                <span className="flex items-center gap-2 text-sm"><Sparkles className="w-4 h-4 text-primary" />NATORIQ recommande une relance : ce client a un solde en retard.</span>
                <Button size="sm" className="rounded-full shrink-0" onClick={() => navigate("/relances")} data-testid="client-reco-action"><BellRing className="w-3.5 h-3.5 mr-1" />Relancer</Button>
              </div>
            )}

            <div className="text-sm font-semibold mt-1">Chronologie</div>
            <div className="max-h-64 overflow-auto space-y-1.5 pr-1">
              {detail.timeline.length === 0 ? <p className="text-sm text-muted-foreground">Aucune activité.</p> :
                detail.timeline.map((t, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 border-b border-border py-1.5 text-sm" data-testid={`timeline-${t.type}-${i}`}>
                    <span className="flex items-center gap-2">
                      {t.type === "payment" ? <CreditCard className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-muted-foreground" />}
                      {t.label}<span className="text-xs text-muted-foreground">{t.date ? fmtDate(t.date) : ""}</span>
                    </span>
                    <span className={t.type === "payment" ? "text-emerald-600 font-medium" : ""}><Money value={t.amount || 0} cur={cur} /></span>
                  </div>
                ))}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
